import type { FaqItem } from './services';

export type ContentBlock = {
  title: string;
  body: string;
};

export type CostRow = {
  tier: string;
  cost: string;
  includes: string;
};

export type BreakdownItem = {
  title: string;
  range: string;
  body: string;
};

export type MaterialGroup = {
  title: string;
  intro: string;
  options: { name: string; range: string; note: string }[];
};

/**
 * Deep kitchen remodeling resource for /services/kitchen-remodeling.
 * Ranges are 2026 Texas planning estimates - not quotes or "real-time" pricing.
 * Original Epic copy; HN used only as depth/IA reference.
 */
export const KITCHEN_PAGE = {
  metaTitle: 'Kitchen Remodeling in Texas | 2026 Cost Guide & Quotes | Epic Home Pros',
  metaDescription:
    '2026 Texas kitchen remodel cost guide with planning tiers, component breakdowns, materials, and timelines. Free on-site estimates in Austin, Dallas, Houston, and San Antonio.',

  hero: {
    eyebrow: 'Texas Kitchen Remodeling',
    title: 'Kitchen Remodeling Quotes in Texas',
    lead:
      'From a minor refresh to a full structural gut. Plan with a clear 2026 Texas cost guide, then get a free on-site estimate for your house.',
  },

  overview: {
    title: 'Kitchen remodeling for Texas homeowners',
    paragraphs: [
      'A kitchen remodel can mean a focused update in the same footprint, or a larger transformation that changes how you cook, store, and gather. Most projects mix finish upgrades with practical improvements to layout, lighting, and work zones.',
      'Published 2026 Texas estimates vary widely by scope and materials. Use the planning ranges on this page to frame early conversations. Your written estimate after an on-site visit is the number that applies to your kitchen.',
      'Epic Home Pros helps homeowners start with one request. We follow up to schedule a visit, talk through options, and prepare a free written estimate. From there, you decide whether to move forward.',
    ],
  },

  cost: {
    title: '2026 Texas Kitchen Remodeling Cost Guide',
    intro:
      'These figures are planning ranges for Texas kitchen projects in 2026 - not a quote for your house and not real-time pricing. Actual cost depends on size, materials, labor, existing conditions, and whether plumbing, electrical, or structural work is involved.',
    disclaimer:
      'Two kitchens of the same square footage can price very differently. Use the tiers below to set expectations, then confirm with a free on-site estimate.',
    rows: [
      {
        tier: 'Minor / Refresh',
        cost: '$10,000 - $25,000',
        includes: 'Paint, hardware, fixtures, and limited countertop or cabinet touch-ups. Same layout.',
      },
      {
        tier: 'Cosmetic',
        cost: '$25,000 - $45,000',
        includes: 'Refacing or repainting, countertops, backsplash, lighting. Mostly same footprint.',
      },
      {
        tier: 'Mid-Range',
        cost: '$45,000 - $90,000',
        includes: 'New cabinets, countertops, appliances, flooring, and lighting upgrades.',
      },
      {
        tier: 'High-End',
        cost: '$90,000 - $150,000',
        includes: 'Custom cabinetry, premium stone, premium appliances, and layout changes.',
      },
      {
        tier: 'Luxury / Structural',
        cost: '$150,000+',
        includes: 'Full gut, wall changes, plumbing or gas relocation, and structural work.',
      },
    ] as CostRow[],
    factorsTitle: 'What influences kitchen remodel cost',
    factorsIntro: 'These are the usual drivers that move a project between tiers:',
    factors: [
      'Overall scope - refresh vs full replacement vs structural change',
      'Kitchen size and linear feet of cabinetry',
      'Cabinet construction and finish level',
      'Countertop material and edge details',
      'Appliance package and venting or fuel-type changes',
      'Layout changes that move plumbing, gas, or electrical',
      'Flooring replacement and transitions into other rooms',
      'Lighting plan complexity',
      'Structural openings or wall removal',
      'Permits, inspections, and contingency for hidden conditions',
    ],
  },

  breakdown: {
    title: 'Kitchen remodel cost breakdown',
    intro:
      'Where the money goes depends on quality level and how much of the room you replace. The mid-range component ranges below are typical Texas planning figures for 2026. They are approximate and stack differently on every house.',
    items: [
      {
        title: 'Cabinets',
        range: '$4,000 - $18,000+',
        body: 'Often the largest single line item. Stock, semi-custom, and custom paths change price quickly. Refacing costs less when boxes are sound.',
      },
      {
        title: 'Countertops',
        range: '$1,700 - $7,000+',
        body: 'Material, thickness, edge profile, island size, and sink cutouts drive the number. Premium stone sits above laminate and entry quartz.',
      },
      {
        title: 'Appliances',
        range: '$6,800 - $15,000+',
        body: 'Ranges from standard packages to professional suites. Ventilation, fuel type, and panel-ready units affect both cost and cabinetry.',
      },
      {
        title: 'Flooring',
        range: 'Varies by material and square footage',
        body: 'LVP, tile, and engineered wood are common. Transitions into adjoining rooms and underlayment add scope.',
      },
      {
        title: 'Plumbing',
        range: 'Fixture-only to full relocation',
        body: 'Sinks, faucets, disposal, and dishwasher connections are common. Moving a sink or adding a prep sink increases rough-in work.',
      },
      {
        title: 'Electrical',
        range: 'Lighting circuits to panel upgrades',
        body: 'New outlets, under-cabinet circuits, and dedicated appliance lines are typical. Older homes may need more capacity.',
      },
      {
        title: 'Backsplash',
        range: 'Tile or slab finishes',
        body: 'Protects walls and finishes the line between counters and uppers. Pattern complexity and slab pieces change labor.',
      },
      {
        title: 'Lighting',
        range: 'Recessed, pendants, task layers',
        body: 'Layered lighting improves usability more than a single flush fixture. Island pendants and under-cabinet LEDs are common upgrades.',
      },
      {
        title: 'Hardware',
        range: 'Pulls, hinges, soft-close upgrades',
        body: 'A smaller line item that still affects daily feel. Soft-close hardware is often paired with new or refreshed cabinets.',
      },
      {
        title: 'Painting',
        range: 'Walls, trim, ceilings',
        body: 'Finishes the remodel and ties new cabinets to adjoining rooms. Cabinet painting belongs in cosmetic tiers when boxes stay.',
      },
      {
        title: 'Demolition',
        range: 'Surfaces only to full gut',
        body: 'Removing cabinets, flooring, and finishes creates disposal and protection work. Full guts take longer and cost more.',
      },
      {
        title: 'Labor / project management',
        range: '$5,000 - $15,000+',
        body: 'Skilled install, sequencing, and coordination across trades. Labor share rises when layout or structural work is involved.',
      },
      {
        title: 'Permits',
        range: 'When required by scope',
        body: 'Like-for-like cosmetic work often needs fewer permits than plumbing, gas, electrical, or wall changes. Requirements vary by city.',
      },
      {
        title: 'Structural work',
        range: 'Only when openings or loads change',
        body: 'Removing walls, adding beams, or altering support changes engineering, inspections, and schedule.',
      },
      {
        title: 'Contingency',
        range: 'Plan a buffer',
        body: 'Hidden water damage, outdated wiring, or uneven floors show up after demolition. A contingency keeps the project from stalling.',
      },
    ] as BreakdownItem[],
  },

  materials: {
    title: 'Materials and finish options',
    intro:
      'Material choices usually swing budget more than square footage alone. Bring must-haves and nice-to-haves to the visit so options can be priced accurately.',
    groups: [
      {
        title: 'Cabinet options and pricing',
        intro:
          'Cabinet path is often the biggest decision. Construction quality, door style, and finish matter as much as brand names.',
        options: [
          {
            name: 'Stock',
            range: 'Lower entry cost',
            note: 'Faster availability and fixed sizes. Best when the layout fits standard modules.',
          },
          {
            name: 'Semi-custom',
            range: 'About $12,000 - $25,000 for many Texas mid-range kitchens',
            note: 'More size flexibility and finish choices while staying below full custom.',
          },
          {
            name: 'Custom',
            range: 'About $30,000 - $60,000+ depending on size and detail',
            note: 'Built for the room - useful for unusual footprints, tall pantries, or specialty storage.',
          },
          {
            name: 'Painted finishes',
            range: 'Varies by prep and sheen',
            note: 'Popular for bright kitchens. Touch-up plans matter in high-traffic homes.',
          },
          {
            name: 'Oak and walnut',
            range: 'Species and grade driven',
            note: 'Natural wood grain reads warmer. Grain direction and stain level affect the look.',
          },
          {
            name: 'Plywood vs MDF',
            range: 'Construction detail',
            note: 'Box and door construction affect durability and moisture resistance. Ask what is behind the finish.',
          },
        ],
      },
      {
        title: 'Countertop options and pricing',
        intro: 'Counters take daily heat, cutting, and cleaning. Material choice should match how you cook.',
        options: [
          {
            name: 'Laminate',
            range: 'Budget-friendly',
            note: 'Lower cost and many patterns. Edges and seams are more visible than stone.',
          },
          {
            name: 'Quartz',
            range: 'Mid to premium',
            note: 'Consistent patterns and lower maintenance than many natural stones.',
          },
          {
            name: 'Granite',
            range: 'Mid to premium',
            note: 'Natural variation. Sealing and edge choices affect long-term care.',
          },
          {
            name: 'Quartzite',
            range: 'Premium',
            note: 'Hard natural stone with distinctive movement. Fabrication detail matters.',
          },
          {
            name: 'Marble',
            range: 'Premium',
            note: 'Soft visual movement. More sensitive to etching and staining in busy kitchens.',
          },
        ],
      },
      {
        title: 'Flooring options',
        intro: 'Flooring should handle spills, traffic, and transitions into living spaces.',
        options: [
          {
            name: 'LVP',
            range: 'Common mid-range pick',
            note: 'Comfortable underfoot and practical for busy households.',
          },
          {
            name: 'Tile',
            range: 'Durable and water-tolerant',
            note: 'Good near sinks and dishwashers. Grout color and size change maintenance.',
          },
          {
            name: 'Hardwood / engineered wood',
            range: 'Warm and resale-friendly',
            note: 'Engineered products often handle humidity better than solid hardwood in some homes.',
          },
        ],
      },
      {
        title: 'Appliance packages',
        intro: 'Confirm sizes and fuel type before cabinets are ordered.',
        options: [
          {
            name: 'Standard',
            range: 'Entry to mid packages',
            note: 'Reliable everyday cooking without specialty ventilation demands.',
          },
          {
            name: 'Mid-range',
            range: 'Feature upgrades',
            note: 'Better ovens, quieter dishwashers, and improved range performance.',
          },
          {
            name: 'Professional / luxury',
            range: 'Highest package cost',
            note: 'Heavier units, stronger ventilation, and cabinetry clearances that must be planned early.',
          },
        ],
      },
    ] as MaterialGroup[],
  },

  timelines: {
    title: 'Kitchen remodel timelines',
    intro:
      'Schedules are approximate for 2026 Texas projects. Cabinet, countertop, and appliance lead times often matter more than demolition days. These are planning windows - not guarantees.',
    rows: [
      { tier: 'Minor refresh', duration: '2 - 4 weeks', note: 'Limited trades and fewer special-order materials.' },
      { tier: 'Cosmetic remodel', duration: '3 - 6 weeks', note: 'Surfaces and lighting with the same layout.' },
      { tier: 'Mid-range', duration: '6 - 10 weeks', note: 'New cabinets and finishes; ordering drives the calendar.' },
      { tier: 'High-end', duration: '8 - 14 weeks', note: 'Custom materials and more finish decisions.' },
      { tier: 'Luxury / structural', duration: '12 - 20+ weeks', note: 'Structural, plumbing, or gas changes plus inspections.' },
    ],
    delaysTitle: 'What commonly extends the schedule',
    delays: [
      'Cabinet manufacturing and delivery windows',
      'Countertop templating and fabrication after cabinets are set',
      'Appliance backorders or size changes mid-project',
      'Permits and inspections when scope requires them',
      'Structural openings, beams, or wall removal',
      'Plumbing or electrical relocation discovered after demolition',
      'Change orders after materials are already ordered',
    ],
  },

  included: {
    title: 'What a kitchen remodel may include',
    intro:
      'Every written estimate should spell out inclusions. The checklist below shows categories that commonly appear - then how work usually sorts into standard, optional, and structural.',
    checklist: [
      'Design and planning',
      'Demolition and haul-away',
      'Cabinets and hardware',
      'Countertops',
      'Backsplash',
      'Flooring',
      'Plumbing fixtures and connections',
      'Electrical and lighting',
      'Appliance placement and connections',
      'Painting and finish work',
      'Cleanup',
      'Final walkthrough',
    ],
    groups: [
      {
        title: 'Standard scope items',
        body: 'Finish and fixture work inside the existing footprint - cabinets or refacing, counters, backsplash, lighting, paint, and appliance swap-outs that fit current openings.',
      },
      {
        title: 'Optional upgrades',
        body: 'Islands, pantry builds, specialty storage, upgraded stone, layered lighting packages, and appliance package jumps that raise comfort without changing the house structure.',
      },
      {
        title: 'Structural or relocation work',
        body: 'Wall openings, beam work, moving sinks or gas lines, adding circuits, or changing load paths. These items need careful on-site review and often more schedule.',
      },
    ] as ContentBlock[],
  },

  projectTypes: {
    title: 'Types of kitchen remodels',
    intro: 'Start by naming the project type. It keeps budget conversations honest and helps the site visit focus on the right questions.',
    items: [
      {
        title: 'Cosmetic remodel',
        body: 'Updates surfaces and fixtures while keeping the layout. Good when the footprint already works.',
      },
      {
        title: 'Full remodel',
        body: 'New cabinets, counters, flooring, and appliances in the same general footprint.',
      },
      {
        title: 'Layout change',
        body: 'Reworks work zones, appliance placement, or openings between rooms when the house allows.',
      },
      {
        title: 'Open-concept conversion',
        body: 'Connects kitchen to living or dining space. May involve wall removal and new sight lines.',
      },
      {
        title: 'Small kitchen remodel',
        body: 'Focuses on storage, lighting, and right-sized appliances rather than forcing a large island.',
      },
      {
        title: 'Large kitchen remodel',
        body: 'Supports dual prep zones, pantry rooms, or multiple seating areas - usually with a clearer material plan to control cost.',
      },
      {
        title: 'Island remodel',
        body: 'Adds or expands an island for prep, seating, or storage. Clearances for traffic and appliance doors are critical.',
      },
      {
        title: 'Cabinet replacement or refacing',
        body: 'Refacing works when boxes are sound. Full replacement is better when sizes or configuration need to change.',
      },
    ] as ContentBlock[],
  },

  styles: {
    title: 'Kitchen layouts',
    intro:
      'Layout shapes daily workflow and remodel cost. During an on-site visit, we can help you decide what fits the room - and what is realistic for the house.',
    items: [
      {
        title: 'Galley',
        body: 'Two parallel runs that maximize efficiency in a narrow footprint. Remodels often focus on storage, lighting, and clearances.',
      },
      {
        title: 'L-shaped',
        body: 'Two adjacent walls of cabinets with open floor space for an island or table. Common in suburban Texas homes.',
      },
      {
        title: 'U-shaped',
        body: 'Three walls of work surface and storage. Strong for serious cooking; remodels often improve corners and appliance placement.',
      },
      {
        title: 'Open-concept',
        body: 'Kitchen connected to living or dining space. May involve removing a wall and balancing island seating with traffic.',
      },
      {
        title: 'Island-centered',
        body: 'Islands add prep space, seating, and storage. Size, electrical, and overhangs should be planned before cabinets are ordered.',
      },
    ] as ContentBlock[],
  },

  process: {
    title: 'Kitchen remodeling process with Epic Home Pros',
    intro:
      'Epic Home Pros is the starting point: one request, an on-site visit, and a free written estimate. If you choose to proceed, design decisions and construction follow from the plan you approve.',
    steps: [
      {
        label: '1. Initial inquiry',
        body: 'Submit the quote form or call. Share your city, a short description of the kitchen, and any must-haves such as an island, new appliances, or a layout change.',
      },
      {
        label: '2. Project discussion',
        body: 'We follow up - typically within one business day - to clarify scope, confirm coverage, and schedule next steps.',
      },
      {
        label: '3. Scope and planning',
        body: 'We review photos if you shared them, schedule a visit, and talk through what is realistic for the room, budget, and timeline.',
      },
      {
        label: '4. On-site estimate',
        body: 'We measure the kitchen, check access and existing conditions, and prepare a written estimate. Estimates are free. You are not obligated to move forward.',
      },
      {
        label: '5. Design and material decisions',
        body: 'If you proceed, you refine cabinet selections, counters, fixtures, appliances, and finish details that lock the final scope.',
      },
      {
        label: '6. Preparation',
        body: 'Before work starts, sequencing, material lead times, and how the kitchen will be taken offline during construction are planned.',
      },
      {
        label: '7. Remodeling and construction',
        body: 'Work moves from demolition through install according to the agreed scope.',
      },
      {
        label: '8. Completion',
        body: 'Final walkthrough and punch items wrap the project based on the plan you approved.',
      },
    ],
  },

  considerations: {
    title: 'What to consider before remodeling',
    intro: 'A little clarity before the site visit makes the estimate more accurate and the project smoother.',
    items: [
      {
        title: 'Layout and workflow',
        body: 'Map how you cook: fridge to sink to cooktop. Note where two people bump into each other today.',
      },
      {
        title: 'Storage needs',
        body: 'List what never fits - small appliances, bulk goods, bakeware - so pantry and drawer solutions get planned early.',
      },
      {
        title: 'Materials',
        body: 'Decide how durable vs decorative you want counters, floors, and cabinet finishes to be for Texas heat, kids, or pets.',
      },
      {
        title: 'Appliances',
        body: 'Confirm sizes and fuel type before cabinetry is ordered. Ventilation paths matter for ranges and cooktops.',
      },
      {
        title: 'Lighting',
        body: 'Plan task light for counters, ambient light for evenings, and accent light for islands or display areas.',
      },
      {
        title: 'Budget bands',
        body: 'Use the 2026 Texas planning tiers on this page to set a target, then let the on-site estimate refine it.',
      },
      {
        title: 'Timeline',
        body: 'Cabinet and stone lead times often shape the schedule more than demolition days. Ask about order windows early.',
      },
      {
        title: 'Plumbing and electrical',
        body: 'Moving a sink, adding circuits, or upgrading panels can change both cost and duration. Flag those goals in the quote request.',
      },
      {
        title: 'Long-term usability',
        body: 'Design for how you will live in five to ten years - clearances, resale appeal, or a growing household.',
      },
    ] as ContentBlock[],
  },

  local: {
    title: 'Kitchen remodeling across Texas',
    intro:
      'Epic Home Pros serves kitchen remodeling projects across major Texas metros. Housing stock and layout habits differ by city - an on-site visit keeps the plan grounded in your house.',
    cities: [
      {
        name: 'Austin',
        slug: 'austin',
        body: 'Austin kitchens range from compact mid-century footprints to open suburban plans. Local projects often balance storage, islands, and connections to living areas.',
      },
      {
        name: 'Dallas',
        slug: 'dallas',
        body: 'Dallas-Fort Worth homes span older in-town layouts and larger suburban kitchens. Remodels frequently update cabinets, counters, and lighting while confirming what plumbing and electrical changes the house can support.',
      },
      {
        name: 'Houston',
        slug: 'houston',
        body: 'Greater Houston kitchens benefit from finish choices that handle humidity and busy household use. Open plans and outdoor connections are common planning topics.',
      },
      {
        name: 'San Antonio',
        slug: 'san-antonio',
        body: 'San Antonio remodels often focus on durable surfaces, better storage, and layouts that fit how the household cooks and gathers.',
      },
    ],
    closing:
      'Nearby markets such as Fort Worth, Arlington, and Houston-area suburbs may also be covered. Request a quote with your city and we will confirm coverage for your address.',
  },

  gallery: {
    eyebrow: 'Kitchen inspiration',
    title: 'Layouts and finishes homeowners are planning',
    images: [
      {
        src: 'https://images.unsplash.com/photo-1556912173-46c336c7fd55?w=1200&q=80&auto=format&fit=crop',
        alt: 'Bright modern kitchen with white cabinets',
      },
      {
        src: 'https://images.unsplash.com/photo-1682888818650-0a7cbb35287d?w=1200&q=80&auto=format&fit=crop',
        alt: 'Kitchen island with stone counters',
      },
      {
        src: 'https://images.unsplash.com/photo-1484154218962-a197022b5858?w=1200&q=80&auto=format&fit=crop',
        alt: 'Open kitchen with wood cabinets and island seating',
      },
      {
        src: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1200&q=80&auto=format&fit=crop',
        alt: 'Custom cabinetry kitchen',
      },
    ],
  },

  faqs: [
    {
      question: 'How much does a kitchen remodel cost in Texas in 2026?',
      answer:
        'Planning ranges on this site run about $10,000-$25,000 for a minor refresh, $25,000-$45,000 for cosmetic work, $45,000-$90,000 for mid-range, $90,000-$150,000 for high-end, and $150,000+ for luxury or structural projects. These are planning estimates, not quotes. Your written estimate after an on-site visit is the number for your house.',
    },
    {
      question: 'Are these prices quotes or planning ranges?',
      answer:
        'Planning ranges only. Published 2026 Texas figures vary by what is included. A firm price needs measurements, existing conditions, and the finishes you want.',
    },
    {
      question: 'What is usually included in a kitchen remodel?',
      answer:
        'Scopes often include design planning, demolition, cabinets, counters, backsplash, flooring, plumbing, electrical, lighting, appliances, paint, cleanup, and a final walkthrough. Optional and structural items are listed separately on the written estimate.',
    },
    {
      question: 'How long does a kitchen remodel take?',
      answer:
        'Approximate windows: 2-4 weeks for a minor refresh, 3-6 for cosmetic, 6-10 for mid-range, 8-14 for high-end, and 12-20+ weeks for structural projects. Lead times for cabinets, stone, and appliances often set the pace.',
    },
    {
      question: 'Do I need to change my kitchen layout?',
      answer:
        'Not always. Many remodels keep the footprint and improve finishes, lighting, and storage. Layout changes help when traffic is awkward or you want an open connection to living space, but they add plumbing, electrical, or structural cost.',
    },
    {
      question: 'Should I replace cabinets or reface them?',
      answer:
        'Refacing or new doors can work when boxes are sound and the layout still fits. Full replacement is better when you need different sizes, deeper drawers, a new island, or a different configuration.',
    },
    {
      question: 'When should I choose countertops and appliances?',
      answer:
        'Appliance sizes and fuel type should be confirmed before cabinets are finalized. Countertop material can be selected alongside cabinets so edges, overhangs, and sink cutouts align.',
    },
    {
      question: 'Is the kitchen remodeling estimate free?',
      answer:
        'Yes. Epic Home Pros provides a free on-site visit and written estimate with no obligation to move forward.',
    },
    {
      question: 'What happens after I request a kitchen quote?',
      answer:
        'We aim to reply within one business day, confirm coverage for your city, and schedule an on-site visit. We measure, discuss scope, and send a written estimate. You decide whether to move forward.',
    },
    {
      question: 'Can I remodel a small kitchen without expanding the footprint?',
      answer:
        'Yes. Small kitchens often improve most from smarter storage, brighter layered lighting, continuous flooring, and right-sized appliances.',
    },
    {
      question: 'Which Texas cities do you cover for kitchen remodeling?',
      answer:
        'We serve kitchen projects in Austin, Dallas, Houston, San Antonio, and surrounding metros when coverage allows. Include your city on the quote form and we will confirm.',
    },
    {
      question: 'Will someone give me a firm price over the phone?',
      answer:
        'Phone conversation can help set expectations using planning ranges, but a firm written estimate needs an on-site look at measurements, existing conditions, and finishes.',
    },
  ] as FaqItem[],

  midCta: {
    title: 'Ready to price your kitchen?',
    lead: 'Free on-site estimate. No obligation.',
  },
  finalCta: {
    title: 'Start your Texas kitchen remodel quote',
    lead: 'Tell us what you are planning. We will help you take the next step with a free on-site estimate.',
  },
} as const;
